/**
 * Field's scores (docs/plans/departments/field.md, Lj Oct 11: "give the user hard-coded flexibility: they pick what's
 * important, but the data must be accurate"). Field builds goals from a FIXED preset library — no free-form metrics —
 * and every preset is counted here from real data, with what it counted and what it left out so each number can be
 * checked:
 *   earnedPerHour   labour in the price × share of the estimate finished ÷ hours on site (T&M jobs: hours × the bill
 *                   rate); target = the jobs' own targets by job type, blended by hours (jobpay.js)
 *   onEstimate      Efficiency — estimated ÷ actual hours on the tasks finished
 *   productiveShare this department's hours (setup, cleanup, runs, travel included) ÷ every hour worked, any department
 *   hoursWorked     hours on the department's tasks
 *   tasksDone       tasks finished (not checklists, not quick tasks)
 *   photosTaken     photos on the department's tasks, by the day they were taken
 *   setupCleanup    minutes on the setup / cleanup checklists and quick tasks
 *   jobMargin       finished jobs' margin — the job cost screen's calculation (costing.js / profit.js)
 * plus tag:callback and ops:storeRuns (deptgoals.js). Each returns { value, rows, basis, notes } — `basis` = what was
 * counted, `notes` = what was left out (or counted a different way). Pure — no I/O.
 *
 * `data` = what deptGoalsView reads; job margin also wants the job cost inputs: `jobCost(project)` (the client's
 * useProjectProfit — the same function the job cost screen draws) or the raw ones (jobs, sessions, expenses, sections,
 * pay, me, overhead, jobPays, policy, equipmentRates).
 */
import { workDepartments } from './workdept.js';
import { departmentOf } from './hierarchy.js';
import { contractValueOf, personRates, moneyShort } from './profit.js';
import { earnedByJobType, targetFromText } from './jobpay.js';
import { computeProjectCosting } from './costing.js';
import { taskPhotoList } from './taskphotos.js';
import { projectStatus } from './projectstatus.js';
import { localDateStr } from './time.js';
import { isOverheadTask } from './deptscore.js';
import { canonTag } from './tags.js';

const H = 3600000;
const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const inWin = (date, w) => !!date && date >= w.from && date <= w.to;
const doneDate = (t) => (t?.status === 'done' && t.completedAt ? localDateStr(t.completedAt) : null);
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const hrs = (ms) => { const h = ms / H; return `${h >= 10 ? Math.round(h) : Math.round(h * 10) / 10} h`; };
const mins = (ms) => `${Math.round(ms / 60000)} min`;

/** The builder's Field library: the metric, its period and the target it starts at (field.md "The goal presets"). */
export const FIELD_PRESETS = Object.freeze([
  { key: 'earnedPerHour', period: 'week', target: 90, targetFrom: { kind: 'jobs' } },   // each job's own target by its type, blended by hours (v0.1.258); $90/h only while no job has one
  { key: 'onEstimate', period: 'week', target: 100 },
  { key: 'productiveShare', period: 'week', target: 80 },
  { key: 'hoursWorked', period: 'week', target: 40 },
  { key: 'tasksDone', period: 'week', target: 25 },
  { key: 'tag:callback', period: 'month', target: 0, direction: 'down' },
  { key: 'ops:storeRuns', period: 'week', target: 3, direction: 'down' },
  { key: 'photosTaken', period: 'week', target: 20 },
  { key: 'setupCleanup', period: 'day', target: 45, direction: 'down' },
  { key: 'jobMargin', period: 'month', target: 35 },
]);

/** The metrics only Field's library adds (merged into DEPT_GOAL_METRICS). */
export const FIELD_GOAL_METRICS = Object.freeze({
  photosTaken: { label: 'Photos taken', unit: '', kind: 'count', direction: 'up', hint: 'photos on this department’s tasks, counted on the day they were taken (the day added when the photo doesn’t say)' },
  setupCleanup: { label: 'Setup + cleanup', unit: 'min', kind: 'rate', direction: 'down', hint: 'minutes on the setup and cleanup checklists and quick tasks — under the limit is 100' },
  jobMargin: { label: 'Job margin', unit: '%', kind: 'rate', direction: 'up', hint: 'jobs finished in the period: profit ÷ revenue, the job cost screen’s numbers (your own unpaid time not costed); jobs with no contract value left out' },
});

/** Your hourly rate from BattleBooks (opsPay): what you're paid an hour, or your shadow rate when you're unpaid. */
export function payRateOf(data = {}) {
  const pay = data.pay && data.me ? data.pay[data.me] : null;
  if (!pay) return null;
  const v = num(personRates(pay).paidOut);
  return v > 0 ? v : null;
}

/** Lookups every Field count needs, built once per call. */
function lookups(dept, data) {
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  const projById = new Map(projects.map((p) => [p.id, p]));
  const jobById = new Map((data.jobs || []).map((j) => [j.id, j]));
  const parents = new Set(tasks.filter((t) => t.parentId && t.estimatedMinutes > 0).map((t) => t.parentId));
  const mineEntry = (e, t, w) => inWin(e?.date, w) && (e.duration || 0) > 0 && wd.forEntry(e, t)?.id === dept?.id;
  const mineTask = (t) => wd.forTask(t)?.id === dept?.id;
  const jobOf = (p) => (p?.bookProjectId ? jobById.get(p.bookProjectId) || null : null);
  return { tasks, projects, departments, wd, projById, jobOf, parents, mineEntry, mineTask };
}

/**
 * Earned per hour, by JOB TYPE (Lj, Oct 11: "the task and pay percentage, based on project type" — jobpay.js): what the
 * work finished in the window earned ÷ every hour on the department's tasks (overhead included — it's the hour on site
 * that has to pay).
 *   fixed / product  the LABOUR in the price (the bid's labour $, else contract × labour %) × the finished task's share
 *                    of the job's whole estimate — labour, so it compares with the job's labour rate target
 *   hourly (T&M)     hours × the bill rate (the project's, else the department's, else its role rates; travel tasks at
 *                    the project's travel rate) — never your pay rate
 *   internal / personal earn nothing by the hour (their hours still count) and have no target
 * A job with no type: a contract value makes it fixed, else T&M. Each job carries its own target (jobRateTarget) and the
 * department's target is their hours-weighted blend (`jobTarget`) — "$82/h · from your jobs' bids and rates".
 */
export function fieldEarnedPerHour(dept, w, data = {}) {
  const L = lookups(dept, data);
  // the one count (jobpay.js earnedByJobType) — the department score, the scrubber and the Daily Log read the same
  const E = earnedByJobType({
    tasks: L.tasks, projects: L.projects, departments: L.departments, jobs: data.jobs || [], policy: data.overhead?.policy || data.policy || null,
    from: w.from, to: w.to, entryIn: (e, t) => L.wd.forEntry(e, t)?.id === dept?.id, taskIn: L.mineTask,
  });
  const { ms, earned, valued, fixedTasks, noEst, tmRated, tmNoRate, noneMs, byJob, msByProject, jobInfo } = E;
  const noLabour = new Set(E.noLabour);
  const name = (pid) => L.projById.get(pid)?.name || 'A job';
  const perH = (v) => `${moneyShort(Math.round(v))}/h`;
  const vsText = (pid, earnedHere) => {
    const j = jobInfo(pid); const jms = msByProject.get(pid) || 0;
    if (!j.target?.rate) return '';
    const got = jms > 0 ? ` · ${perH(earnedHere / (jms / H))}` : '';
    return `${got} vs ${perH(j.target.rate)} (${targetFromText(j.target.from, j.labour?.pct)})`;
  };
  const rows = [...byJob.values()].sort((a, b) => (b.fixed + b.tm) - (a.fixed + a.tm)).map((r) => ({
    projectId: r.pid,
    title: `${name(r.pid)} · ${moneyShort(Math.round(r.fixed + r.tm))}${vsText(r.pid, r.fixed + r.tm)}${r.tasks ? ` · ${plural(r.tasks, 'task')} finished` : ''}${r.tmMs ? ` · T&M ${hrs(r.tmMs)} billed` : ''}`,
    target: jobInfo(r.pid).target?.rate ?? null,
  }));
  const notes = [];
  if (fixedTasks) notes.push('fixed / product jobs count the labour in the price (the bid’s labour, else contract × labour %) — so each compares with its own labour rate');
  if (tmRated.length) notes.push(`${plural(tmRated.length, 'T&M job')} counted at the billable rate (hours × the project’s rate, else the department’s) — not your pay rate`);
  if (noEst) notes.push(`${plural(noEst, 'finished task')} with no estimate left out — they earn no share of the contract`);
  if (noLabour.size) notes.push(`${plural(noLabour.size, 'fixed job')} with no contract value or bid labour (${[...noLabour].map(name).join(', ')}): finished tasks earn nothing — set the contract`);
  if (tmNoRate.length) notes.push(`${plural(tmNoRate.length, 'T&M job')} with no bill rate (${tmNoRate.map((x) => name(x.pid)).join(', ')}): ${hrs(tmNoRate.reduce((a, x) => a + x.ms, 0))} counted, nothing earned — set the project’s rate`);
  if (noneMs > 0) notes.push(`${hrs(noneMs)} on internal / personal jobs counted — they earn nothing by the hour`);
  if (ms > 0 && !valued) notes.push('nothing to price yet: no finished task on a job with a contract value, and no T&M job with a rate');
  // per job: what it earned ÷ its hours on site (overhead on the job included) — the dashboard's per-job table
  const jobs = [...new Set([...msByProject.keys(), ...byJob.keys()])].map((pid) => {
    const r = byJob.get(pid); const j = jobInfo(pid); const jms = msByProject.get(pid) || 0;
    const e = r ? r.fixed + r.tm : 0;
    return { projectId: pid, kind: j.kind, type: j.type, earned: Math.round(e), ms: jms, perHour: jms > 0 && (r || j.kind === 'fixed') && j.kind !== 'none' ? Math.round(e / (jms / H)) : null, tasks: r?.tasks || 0, rate: r?.rate || null,
      target: j.target?.rate ?? null, targetFrom: j.target?.from ?? null, targetWhy: j.target?.why ?? null, labourPct: j.labour?.pct ?? null };
  }).sort((a, b) => b.ms - a.ms);
  return { value: E.value, rows, basis: ms > 0 ? `${moneyShort(Math.round(earned))} earned ÷ ${hrs(ms)} on site` : 'no hours here yet', notes, earned, ms, jobs, jobTarget: blendJobTargets(jobs, () => liveJobTargets(dept, data, L, jobInfo)) };
}

/**
 * The department's Earned-per-hour target: the hours-weighted blend of its jobs' own targets (jobs with hours in the
 * window and a target; internal / personal left out). No hours yet → the plain mean over its live jobs with a target.
 * → { value, jobs, from: 'hours' | 'live' } | null
 */
function blendJobTargets(jobs, live) {
  const worked = jobs.filter((j) => j.kind !== 'none' && j.target > 0 && j.ms > 0);
  const hours = worked.reduce((a, j) => a + j.ms, 0);
  if (hours > 0) return { value: Math.round(worked.reduce((a, j) => a + j.target * j.ms, 0) / hours), jobs: worked.length, from: 'hours' };
  const l = live();
  return l.length ? { value: Math.round(l.reduce((a, x) => a + x, 0) / l.length), jobs: l.length, from: 'live' } : null;
}
const LIVE_JOB = (p) => p && !p.deletedAt && !p.archived && !['archived', 'completed', 'cancelled', 'on_hold'].includes(p.status);
function liveJobTargets(dept, data, L, jobInfo) {
  return L.projects.filter((p) => LIVE_JOB(p) && departmentOf(p, L.departments)?.id === dept?.id).map((p) => jobInfo(p.id).target?.rate).filter((v) => v > 0);
}

/** Efficiency: estimated ÷ actual hours on the tasks finished in the window (100% = on the estimate). */
export function fieldEfficiency(dept, w, data = {}) {
  const L = lookups(dept, data);
  let estMin = 0; let actualMs = 0; let noEst = 0; let noTime = 0; let steps = 0;
  const rows = [];
  for (const t of L.tasks) {
    const d = doneDate(t);
    if (!inWin(d, w) || isOverheadTask(t) || !L.mineTask(t) || L.parents.has(t.id)) continue;
    if (!(t.estimatedMinutes > 0)) { noEst += 1; continue; }
    if (t.runId) { steps += 1; continue; }
    if (!((t.timeSpent || 0) >= 60000)) { noTime += 1; continue; }
    estMin += t.estimatedMinutes; actualMs += t.timeSpent;
    rows.push({ taskId: t.id, title: `${t.title} · ${hrs(t.estimatedMinutes * 60000)} est, ${hrs(t.timeSpent)} actual`, date: d });
  }
  const notes = [];
  if (noEst) notes.push(`${plural(noEst, 'finished task')} with no estimate left out`);
  if (noTime) notes.push(`${plural(noTime, 'finished task')} with under a minute tracked left out`);
  if (steps) notes.push(`${plural(steps, 'routine step')} left out`);
  return {
    value: actualMs > 0 ? Math.round(((estMin * 60000) / actualMs) * 100) : null, rows,
    basis: rows.length ? `${plural(rows.length, 'finished task')}: ${hrs(estMin * 60000)} estimated ÷ ${hrs(actualMs)} actual` : 'no finished task with an estimate and time yet', notes,
  };
}

/** Which overhead a task is: Setup · Cleanup · Material runs · Travel · Other quick tasks (job work never asks). */
export const OVERHEAD_KIND = (t) => {
  if (t.bucket === 'setup' || t.quickTaskId === 'setup') return 'Setup';
  if (t.bucket === 'cleanup' || t.quickTaskId === 'cleanup') return 'Cleanup';
  const tags = (t.tags || []).map((x) => canonTag(String(x)));
  if (t.quickTaskId === 'material-run' || tags.includes('materials')) return 'Material runs';
  if (t.quickTaskId === 'travel' || tags.includes('travel')) return 'Travel';
  return 'Other quick tasks';
};

/**
 * Productive share (Lj, Oct 11: "anything field related should be accounted for in the bid, so it's not overhead — if
 * I'm doing an invoice, that's overhead"): hours on this department's work — job tasks, setup, cleanup, material runs,
 * travel, all of it — ÷ every hour worked in the window, any department. The rest is other work (Finance, Dev, General…).
 * Rows: Field's own split (job tasks / setup + cleanup / runs + travel), then the other work by department.
 */
export function fieldProductiveShare(dept, w, data = {}) {
  const L = lookups(dept, data);
  let all = 0; let mine = 0;
  const byKind = new Map(); const byDept = new Map();
  for (const t of L.tasks) {
    for (const e of t.timeEntries || []) {
      if (!inWin(e?.date, w) || !((e.duration || 0) > 0)) continue;
      all += e.duration;
      const d = L.wd.forEntry(e, t);
      if (d?.id === dept?.id) {
        mine += e.duration;
        const k = FIELD_WORK_KIND(t);
        byKind.set(k, (byKind.get(k) || 0) + e.duration);
      } else {
        const name = d?.name || 'General';
        byDept.set(name, (byDept.get(name) || 0) + e.duration);
      }
    }
  }
  const name = String(dept?.name || 'Field');
  const other = all - mine;
  const rows = [
    ...[...byKind].sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ title: `${name} · ${k} · ${hrs(v)}`, label: k, group: 'mine' })),
    ...[...byDept].sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ title: `Other work · ${k} · ${hrs(v)}`, label: k, group: 'other' })),
  ];
  return {
    value: all > 0 ? Math.round((mine / all) * 100) : null, rows, mine, all,
    basis: all > 0 ? `${hrs(mine)} on ${name} work (job tasks, setup, cleanup, runs, travel) of ${hrs(all)} worked; other work ${hrs(other)}` : 'no hours worked yet',
    notes: ['clock time with no task running isn’t counted'],
  };
}

/** Field's own work, split three ways for the productive bar: job tasks · setup + cleanup · runs + travel (+ other quick tasks). */
export const FIELD_WORK_KIND = (t) => {
  if (!isOverheadTask(t)) return 'Job tasks';
  const k = OVERHEAD_KIND(t);
  return k === 'Setup' || k === 'Cleanup' ? 'Setup + cleanup' : k === 'Material runs' || k === 'Travel' ? 'Runs + travel' : 'Other quick tasks';
};

/** Hours worked: every hour on the department's tasks, by job. */
export function fieldHoursWorked(dept, w, data = {}) {
  const L = lookups(dept, data);
  let ms = 0;
  const byJob = new Map();
  for (const t of L.tasks) {
    for (const e of t.timeEntries || []) {
      if (!L.mineEntry(e, t, w)) continue;
      ms += e.duration;
      const k = t.projectId || '';
      byJob.set(k, (byJob.get(k) || 0) + e.duration);
    }
  }
  const rows = [...byJob].sort((a, b) => b[1] - a[1]).map(([pid, v]) => ({ projectId: pid || null, title: `${pid ? L.projById.get(pid)?.name || 'A job' : 'No job'} · ${hrs(v)}` }));
  return { value: ms > 0 ? Math.round((ms / H) * 10) / 10 : 0, rows, basis: `${hrs(ms)} on this department’s tasks (clock time with no task running isn’t counted)`, notes: [] };
}

/** Tasks finished: the department's tasks finished in the window — not checklists, not quick tasks. */
export function fieldTasksDone(dept, w, data = {}) {
  const L = lookups(dept, data);
  const rows = []; let over = 0;
  for (const t of L.tasks) {
    const d = doneDate(t);
    if (!inWin(d, w) || !L.mineTask(t)) continue;
    if (t.quickTaskId || t.bucket) { over += 1; continue; }
    rows.push({ taskId: t.id, title: t.title, date: d });
  }
  rows.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  return { value: rows.length, rows, basis: `${plural(rows.length, 'task')} finished`, notes: over ? [`${plural(over, 'checklist step / quick task', 'checklist steps / quick tasks')} not counted`] : [] };
}

/** Photos taken: photos on the department's tasks, on the day they were taken (else the day added). */
export function fieldPhotosTaken(dept, w, data = {}) {
  const L = lookups(dept, data);
  const rows = []; let byAdded = 0; let undated = 0;
  for (const t of L.tasks) {
    if (t.deletedAt || !L.mineTask(t)) continue;
    for (const p of taskPhotoList(t)) {
      if (!(p.at > 0)) { undated += 1; continue; }
      const d = localDateStr(p.at);
      if (!inWin(d, w)) continue;
      if (!p.takenAt) byAdded += 1;
      rows.push({ taskId: t.id, title: `${t.title}${p.caption ? ` · “${p.caption}”` : ` · ${p.tag}`}`, date: d, at: p.at });
    }
  }
  rows.sort((a, b) => b.at - a.at);
  const notes = [];
  if (byAdded) notes.push(`${plural(byAdded, 'photo')} with no taken time counted on the day added`);
  if (undated) notes.push(`${plural(undated, 'older photo')} with no time at all left out`);
  return { value: rows.length, rows, basis: `${plural(rows.length, 'photo')} on this department’s tasks, by the day taken`, notes };
}

/** Setup + cleanup: minutes on the setup / cleanup checklists and quick tasks (a limit — under it scores 100). */
export function fieldSetupCleanup(dept, w, data = {}) {
  const L = lookups(dept, data);
  const by = new Map(); let setup = 0; let cleanup = 0;
  for (const t of L.tasks) {
    const k = OVERHEAD_KIND(t);
    if ((k !== 'Setup' && k !== 'Cleanup') || !(t.bucket || t.quickTaskId)) continue;
    for (const e of t.timeEntries || []) {
      if (!L.mineEntry(e, t, w)) continue;
      if (k === 'Setup') setup += e.duration; else cleanup += e.duration;
      const key = `${k}|${t.projectId || ''}|${e.date}`;
      by.set(key, (by.get(key) || 0) + e.duration);
    }
  }
  const rows = [...by].map(([key, v]) => { const [k, pid, date] = key.split('|'); return { title: `${k} · ${pid ? L.projById.get(pid)?.name || 'A job' : 'No job'} · ${mins(v)}`, date, projectId: pid || null }; })
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  return { value: Math.round((setup + cleanup) / 60000), rows, basis: `setup ${mins(setup)} + cleanup ${mins(cleanup)}`, notes: [] };
}

/** One project's job cost (the job cost screen's calculation): the caller's `jobCost`, else worked out here. */
export function fieldJobCost(p, data = {}) { return jobCostOf(p, data, lookups(null, data)); }
function jobCostOf(p, data, L) {
  if (typeof data.jobCost === 'function') return data.jobCost(p);
  return computeProjectCosting({
    project: p, tasks: L.tasks, sessions: data.sessions || [], expenses: data.expenses || [], sections: (data.sections || []).filter((s) => s.projectId === p.id),
    pay: data.pay || {}, job: L.jobOf(p), dept: departmentOf(p, L.departments), me: data.me || null, now: data.now || Date.now(), overhead: data.overhead || null,
    jobPays: data.jobPays || [], policy: data.policy || null, equipmentRates: data.equipmentRates || null,
  });
}

/**
 * Job margin: the jobs of this department finished (completed) in the window — Σ profit ÷ Σ revenue from the job cost
 * screen's calculation (actual: real wages, your own unpaid time not costed; the row shows the if-paid-out margin too).
 * Jobs with no contract value are left out and counted.
 */
export function fieldJobMargin(dept, w, data = {}) {
  const L = lookups(dept, data);
  const finished = L.projects.filter((p) => p && projectStatus(p) === 'completed' && num(p.completedAt) && inWin(localDateStr(num(p.completedAt)), w) && departmentOf(p, L.departments)?.id === dept?.id);
  let rev = 0; let profit = 0; let paid = 0; let noValue = 0; let noRevenue = 0;
  const rows = [];
  for (const p of finished) {
    const contract = contractValueOf(p, L.jobOf(p));
    if (!(contract > 0)) { noValue += 1; continue; }
    const c = jobCostOf(p, data, L)?.cfo;
    const r = num(c?.revenue?.amount);
    if (!c || !(r > 0)) { noRevenue += 1; continue; }
    rev += r; profit += num(c.profit?.actual) || 0; paid += num(c.profit?.paidOut) || 0;
    const pct = (v) => `${Math.round((v / r) * 100)}%`;
    rows.push({ projectId: p.id, title: `${p.name || 'A job'} · ${pct(num(c.profit?.actual) || 0)} · ${pct(num(c.profit?.paidOut) || 0)} if paid out`, date: localDateStr(num(p.completedAt)),
      pct: Math.round(((num(c.profit?.actual) || 0) / r) * 100), paidPct: Math.round(((num(c.profit?.paidOut) || 0) / r) * 100), revenue: r, profit: num(c.profit?.actual) || 0 });
  }
  const notes = [];
  if (noValue) notes.push(`${plural(noValue, 'finished job')} with no contract value left out`);
  if (noRevenue) notes.push(`${plural(noRevenue, 'finished job')} with nothing earned or booked left out`);
  return {
    value: rev > 0 ? Math.round((profit / rev) * 100) : null, rows, notes,
    basis: rows.length ? `${plural(rows.length, 'finished job')}: ${moneyShort(Math.round(profit))} profit on ${moneyShort(Math.round(rev))}${paid !== profit ? ` (${Math.round((paid / rev) * 100)}% if paid out)` : ''}` : 'no job finished with a contract value yet',
  };
}

/** The counts above by goal key. */
export const FIELD_COUNTS = Object.freeze({
  earnedPerHour: fieldEarnedPerHour, onEstimate: fieldEfficiency, productiveShare: fieldProductiveShare, hoursWorked: fieldHoursWorked,
  tasksDone: fieldTasksDone, photosTaken: fieldPhotosTaken, setupCleanup: fieldSetupCleanup, jobMargin: fieldJobMargin,
});
